/**
 * Settings that live on an integration's own document rather than on team
 * settings, catalogued against the vendor group they are configured under.
 * groups.ts mirrors the connection cards; this describes what a merchant can
 * change once a vendor is connected.
 */
import { createSettingMetadataFromGroupDefaults } from "../settings-catalog/metadata";
import type {
  ExternalSettingValueSource,
  SettingAccess,
  SettingAgentEditability,
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
} from "../settings-catalog/types";
import {
  IntegrationsHubSettingGroups,
  type IntegrationsHubSettingGroupId,
} from "./groups";

/** The vendor groups themselves are read-only cards; these are editable. */
const merchantEditableOnVendorPage: SettingAccess = {
  read: { minVisibility: "merchant" },
  write: {
    mode: "grants",
    grants: [{ minVisibility: "merchant", method: "direct" }],
  },
};

const requiresApproval: SettingAgentEditability = {
  mode: "requiresApproval",
  reason:
    "Changes where Redo posts lines in the merchant's ERP, which their finance team reconciles against.",
};

interface IntegrationsHubSettingInput extends SettingRegistryMetadataInput {
  group: IntegrationsHubSettingGroupId;
  /** Path on the integration document, for the integration value adapter. */
  ref: string;
}

const IntegrationsHubRegistry = {
  "netsuite/useDiscountLocationId": {
    group: "integrations.netsuite",
    ref: "netsuite.useDiscountLocationId",
    label: "Match the discount line's location to the returned items",
    description:
      "Where Redo posts the discount line on a NetSuite return authorization. On, it goes to the location of the items being returned; off, it goes to the account's first location. Turn it on if NetSuite rejects a return authorization because its discount line names an inactive location or one on another subsidiary.",
    synonyms: [
      "discount location",
      "discount line location",
      "netsuite discount location",
      "match discount location",
    ],
    access: merchantEditableOnVendorPage,
    agentEditability: requiresApproval,
    valueSemantics: {
      defaultValue: false,
      falseLabel: "The account's first location",
      trueLabel: "The location of the returned items",
    },
  },
} as const satisfies Record<string, IntegrationsHubSettingInput>;

export type IntegrationsHubSettingKey = keyof typeof IntegrationsHubRegistry;

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const IntegrationsHubSettingGroupById = Object.fromEntries(
  IntegrationsHubSettingGroups.map((group) => [group.id, group]),
) as Record<
  IntegrationsHubSettingGroupId,
  (typeof IntegrationsHubSettingGroups)[number]
>;

export const IntegrationsHubSettingDefinitions =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  (Object.keys(IntegrationsHubRegistry) as IntegrationsHubSettingKey[]).map(
    (key) => {
      const definition = IntegrationsHubRegistry[key];
      return {
        id: key,
        groupId: definition.group,
        metadata: createSettingMetadataFromGroupDefaults({
          setting: definition,
          group: IntegrationsHubSettingGroupById[definition.group],
        }),
        valueSource: {
          adapter: "integration",
          ref: definition.ref,
        } satisfies ExternalSettingValueSource,
      };
    },
  ) satisfies readonly SettingDefinition<
    IntegrationsHubSettingKey,
    IntegrationsHubSettingGroupId
  >[];

// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
export const IntegrationsHubSettingMetadataByKey = Object.fromEntries(
  IntegrationsHubSettingDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<IntegrationsHubSettingKey, SettingMetadata>;
